import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Row, useBreakpoints } from "@narmi/design_system";
import { default as ByzantineAddress } from "byzantine/src/Address";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { usePutApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/usePutApplicationApplicantDetailsQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { APPLICATION_RELATIONSHIP, AddressFormData } from "@lending/types";
import { AddressFormFields } from "../../Address/AddressFormFields";
import { buildMailingAddress, createDefaultValues, emptyAddress } from "../../Address/helpers";
import { applyAddressErrors, createAddressSchema } from "../../Address/shared";

function CoApplicantAddress() {
  const { l10n } = useLocalization();
  const { goBack, goNext, startOver } = useFlowNavigation();
  const { s } = useBreakpoints();
  const { showErrorToast, showGenericErrorToast } = useToastsContext();
  const { institution, isLoading: isInstitutionLoading } = useGetInstitutionQuery();
  const askMailingAddress = Boolean(institution.lending_ask_mailing_address);
  const requiredMessage = l10n.getString("error-required", null, "Required");
  const schema = createAddressSchema(l10n, askMailingAddress);

  const { data, isLoading: isApplicantDetailsLoading } = useListApplicationApplicantDetailsQuery();
  const coApplicant = data?.results.find(
    (applicant) => applicant.application_relationship === APPLICATION_RELATIONSHIP.CO_APPLICANT
  );

  const coApplicantName = coApplicant?.first_name ?? "";

  const { mutateAsync: updateCoApplicantAddress, isPending } =
    usePutApplicationApplicantDetailsQuery({
      onSuccess: () => {
        goNext();
      },
      onError: (error) => {
        const { hasFieldErrors, hasToastErrors } = applyAddressErrors(
          error,
          setError,
          showErrorToast,
          requiredMessage
        );
        if (!hasFieldErrors && !hasToastErrors) {
          showGenericErrorToast();
        }
      },
    });

  const { handleSubmit, control, setValue, setError, watch, reset } = useForm<AddressFormData>({
    defaultValues: {
      residential_address: emptyAddress(),
      mailing_address: emptyAddress(),
      hasSameMailingAddress: true,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (coApplicant) {
      reset(createDefaultValues(coApplicant));
    }
  }, [coApplicant, reset]);

  const hasSameMailingAddress = watch("hasSameMailingAddress");

  const onUpdate = (address: ByzantineAddress) => {
    setValue("residential_address", address);
  };

  const onMailingAddressUpdate = (address: ByzantineAddress) => {
    setValue("mailing_address", address);
  };

  async function onSubmit(formData: AddressFormData) {
    if (!formData.residential_address) {
      return;
    }

    if (!coApplicant) {
      showGenericErrorToast();
      startOver();
      return;
    }

    await updateCoApplicantAddress({
      params: {
        path: {
          uuid: coApplicant.id,
        },
      },
      body: {
        ...coApplicant,
        residential_address: { ...formData.residential_address, country_code: "US" },
        mailing_address: buildMailingAddress(
          formData,
          coApplicant.mailing_address,
          askMailingAddress
        ),
      },
    });
  }

  function onClickBack() {
    goBack();
  }

  return (
    <PageLayout isLoading={isApplicantDetailsLoading || isInstitutionLoading}>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "co-applicant-address-heading",
            { name: coApplicantName },
            `What's ${coApplicantName}'s home address?`
          )}
          subheading={l10n.getString(
            "co-applicant-address-subheading",
            null,
            "We need their current address to confirm eligibility."
          )}
        />
        <AddressFormFields
          askMailingAddress={askMailingAddress}
          control={control}
          hasSameMailingAddress={hasSameMailingAddress}
          isBreakpoint={s}
          l10n={l10n}
          onMailingAddressUpdate={onMailingAddressUpdate}
          onResidentialAddressUpdate={onUpdate}
        />
        <Row alignItems="center" justifyContent="space-between">
          <BackButton onClick={onClickBack} />
          <Button
            label={l10n.getString("button-label-next", null, "Next")}
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          />
        </Row>
      </PageContent>
    </PageLayout>
  );
}

export { CoApplicantAddress };
